/**
 * useAnalysisJob.js — Phase 45.
 *
 * Hook that drives the async analysis flow:
 *
 *   POST /api/process-with-mapping  → { job_id, session_id }
 *   poll GET /api/process-jobs/{job_id}  (every 500ms)  → progress
 *   when status == "done"             → return result payload
 *   when user cancels                 → POST /api/process-jobs/{job_id}/cancel
 *
 * Returned state lets the UI render a live progress card (stage,
 * message, current/total, ETA, cancel button).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  isAnalysisJobActive,
  isTransientPollingStatus,
} from "@/utils/polling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 500;
const MAX_TRANSIENT_POLL_FAILURES = 4;

// Iter 92 Phase 6 — collapse the 11 technical stages into 3 macro-stages
// for a calmer UX. Each macro-stage owns a contiguous slice of the 0→100%
// progress bar. The detailed stage labels stay in the backend logs for
// debugging; the user only sees one of the three labels below.
//
// Macro-stages:
//   • caricamento  (0-15%)   — loading_etfs, resolving_isins
//   • analisi     (15-85%)   — merging, building_records, applying_rules,
//                              collecting_candidates, building_summary,
//                              building_log, persisting, canonical_resolve
//   • finalizzazione (85-100%) — building_allocation, building_holdings, done
const _STAGE_TO_MACRO = {
  queued:                ["caricamento", 0],
  loading_etfs:          ["caricamento", 5],
  resolving_isins:       ["caricamento", 15],
  building_records:      ["analisi",     20],
  loading_history:       ["analisi",     25],
  applying_rules:        ["analisi",     35],
  collecting_candidates: ["analisi",     50],
  building_summary:      ["analisi",     60],
  building_log:          ["analisi",     65],
  persisting:            ["analisi",     70],
  persisting_merges:     ["analisi",     70],
  merging:               ["analisi",     75],
  canonical_resolve:     ["analisi",     85],
  building_allocation:   ["finalizzazione", 90],
  building_holdings:     ["finalizzazione", 95],
  finalizing:            ["finalizzazione", 98],
  done:                  ["finalizzazione", 100],
};

const _MACRO_LABELS = {
  caricamento:     "Caricamento",
  analisi:         "Analisi",
  finalizzazione:  "Finalizzazione",
};

/**
 * Map a backend progress packet `{stage, current, total}` to the
 * user-facing `{macroLabel, percent}` for the unified progress bar.
 *
 * `percent` is computed as the BASE percent of the macro-stage plus a
 * smooth ramp inside it driven by current/total when the engine
 * provides them. Always monotonically increasing within a session
 * (the caller clamps to the previous value when needed).
 */
export function mapToMacroProgress(progress) {
  if (!progress) return { macroLabel: "—", percent: 0 };
  const stage = progress.stage || "queued";
  const [macro, base] = _STAGE_TO_MACRO[stage] || ["analisi", 50];
  // Ramp within the macro-stage based on current/total.
  const cur = Number(progress.current) || 0;
  const tot = Number(progress.total) || 0;
  // Next stage's base = lookahead, default to 100 if last.
  const stages = Object.keys(_STAGE_TO_MACRO);
  const stageIdx = stages.indexOf(stage);
  const nextBase = stageIdx >= 0 && stageIdx < stages.length - 1
    ? _STAGE_TO_MACRO[stages[stageIdx + 1]][1]
    : 100;
  const span = Math.max(0, nextBase - base);
  const ramp = (tot > 0 && cur > 0) ? Math.min(1, cur / tot) * span * 0.6 : 0;
  return {
    macroLabel: _MACRO_LABELS[macro] || macro,
    percent:    Math.min(99, Math.round(base + ramp)),
  };
}

// Legacy STAGE_LABELS kept for back-compat with anything that still
// imports it (none expected after Iter 92 P6, but harmless).
export const STAGE_LABELS = {
  queued:               "In coda",
  loading_etfs:         "Caricamento ETF dal catalogo",
  resolving_isins:      "Risoluzione ISIN",
  building_records:     "Indicizzazione holdings",
  loading_history:      "Caricamento storia merge",
  applying_rules:       "Applicazione regole di merge",
  collecting_candidates:"Raccolta candidati issuer",
  persisting_merges:    "Salvataggio merge",
  merging:              "Consolidamento duplicati",
  canonical_resolve:    "Consolidamento entità canoniche",
  building_allocation:  "Costruzione allocazioni",
  building_holdings:    "Costruzione Total Holdings",
  done:                 "Completato",
};

export function useAnalysisJob() {
  const [job, setJob] = useState(null);
  // job shape: { job_id, status, progress: {stage, message, current, total}, started_at, ... }
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollTimer = useRef(null);
  const startedAtMs = useRef(null);
  const consecutivePollFailures = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const poll = useCallback(async (jobId) => {
    try {
      const { data } = await axios.get(`${API}/process-jobs/${jobId}`);
      consecutivePollFailures.current = 0;
      setJob(data);
      if (data.status === "done") {
        setResult(data.result || null);
        stopPolling();
        return;
      }
      if (data.status === "failed") {
        setError(data.error || "Analisi fallita");
        stopPolling();
        return;
      }
      if (data.status === "cancelled") {
        stopPolling();
        return;
      }
      pollTimer.current = setTimeout(() => poll(jobId), POLL_MS);
    } catch (err) {
      const status = err?.response?.status;
      if (isTransientPollingStatus(status)
          && consecutivePollFailures.current < MAX_TRANSIENT_POLL_FAILURES) {
        consecutivePollFailures.current += 1;
        const retryDelay = Math.min(
          POLL_MS * (2 ** (consecutivePollFailures.current - 1)),
          4000,
        );
        pollTimer.current = setTimeout(() => poll(jobId), retryDelay);
        return;
      }
      setError(
        err?.response?.data?.detail || err.message ||
        "Connessione all’analisi interrotta",
      );
      stopPolling();
    }
  }, [stopPolling]);

  const start = useCallback(async (selectedIsins) => {
    setResult(null);
    setError(null);
    consecutivePollFailures.current = 0;
    setJob({ status: "queued", progress: { stage: "queued", message: "Avvio analisi…", current: 0, total: 0 } });
    startedAtMs.current = Date.now();
    try {
      const { data } = await axios.post(`${API}/process-with-mapping`, {
        selected_etfs: selectedIsins,
      });
      pollTimer.current = setTimeout(() => poll(data.job_id), 100);
      return { ok: true, job_id: data.job_id, session_id: data.session_id };
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(detail);
      setJob(null);
      return { ok: false, error: detail };
    }
  }, [poll]);

  const cancel = useCallback(async () => {
    if (!job?.job_id) return;
    try {
      await axios.post(`${API}/process-jobs/${job.job_id}/cancel`);
      // Polling will pick up status=cancelled and stop.
    } catch (err) {
      // The backend job may still be alive (or already finalizing). Keep the
      // status poller running so a failed cancel request cannot strand the UI
      // forever in a stale state.
      setError("Annullamento fallito");
    }
  }, [job]);

  const reset = useCallback(() => {
    stopPolling();
    setJob(null);
    setResult(null);
    setError(null);
    startedAtMs.current = null;
    consecutivePollFailures.current = 0;
  }, [stopPolling]);

  // Cleanup on unmount.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Derived: elapsed seconds (live, recomputed each poll tick).
  const elapsedSec = startedAtMs.current
    ? (Date.now() - startedAtMs.current) / 1000
    : 0;

  // Derived: ETA. Very rough (linear extrapolation from current/total).
  let etaSec = null;
  const cur = job?.progress?.current || 0;
  const tot = job?.progress?.total || 0;
  if (cur > 0 && tot > 0 && cur < tot && elapsedSec > 0.5) {
    etaSec = Math.max(1, Math.round((elapsedSec / cur) * (tot - cur)));
  }

  return {
    job,
    result,
    error,
    elapsedSec,
    etaSec,
    start,
    cancel,
    reset,
    isRunning: isAnalysisJobActive(job?.status),
  };
}
